// REQ-P1-06b: EventDetails read-only API 集約。
// 新規GETは画面内の生fetchではなく、生成型付きAPIクライアント経由に寄せる。

import { api, requestApiJson, type paths } from './client';
import type { operations } from './types.gen';
import { ApiStatusError } from './templates';

type CastingInfoResponse = NonNullable<
  paths['/common/events/{eventId}/casting-info']['get']['responses'][200]['content']
>['application/json'];
type CastingProgressResponse = NonNullable<
  paths['/admin/events/{eventId}/casting-progress']['get']['responses'][200]['content']
>['application/json'];
type EventGroupEventsResponse = NonNullable<
  paths['/events/{eventUuid}/group-events']['get']['responses'][200]['content']
>['application/json'];
type CastingTemplateListResponse = NonNullable<
  paths['/casting-templates']['get']['responses'][200]['content']
>['application/json'];
type CastingTemplateMutationRequest = NonNullable<
  paths['/casting-templates']['post']['requestBody']
>['content']['application/json'];
type EventCastingUpdateRequest = NonNullable<
  paths['/events/{eventUuid}/castings/update']['post']['requestBody']
>['content']['application/json'];
type CastingAssignRequest = NonNullable<
  paths['/admin/events/{eventId}/casting-assign']['post']['requestBody']
>['content']['application/json'];
type BatchCastingAssignRequest = NonNullable<
  paths['/admin/events/{eventId}/casting-assign-batch']['post']['requestBody']
>['content']['application/json'];
type UpdateCastingChiefRequest = NonNullable<
  paths['/admin/events/{eventId}/casting-chief']['post']['requestBody']
>['content']['application/json'];
type UpdateCastingConcertmasterRequest = NonNullable<
  paths['/admin/events/{eventId}/casting-concertmaster']['post']['requestBody']
>['content']['application/json'];
type EventCustomRoleRequest = NonNullable<
  paths['/admin/events/{eventId}/custom-roles']['post']['requestBody']
>['content']['application/json'];
type EventCustomRoleAssignmentsRequest = NonNullable<
  paths['/admin/events/{eventId}/custom-roles/assignments']['post']['requestBody']
>['content']['application/json'];
type EventCastingAssignmentSaveRequest = NonNullable<
  paths['/admin/events/{eventId}/casting-assignment-save']['post']['requestBody']
>['content']['application/json'];
type ApiTextResult = {
  data?: string | unknown;
  error?: string | unknown;
  response: Response;
};
type ChatGroupSettingResponse = NonNullable<
  paths['/admin/events/{groupRootEventUuid}/chat-group-setting']['get']['responses'][200]['content']
>['application/json'];
type EventCancelRequest = NonNullable<
  paths['/admin/events/{eventUuid}/cancel']['post']['requestBody']
>['content']['application/json'];
type EventChatGroupSettingRequest = NonNullable<
  paths['/admin/events/{groupRootEventUuid}/chat-group-setting']['put']['requestBody']
>['content']['application/json'];
type AdminEventPlBooksResponse = NonNullable<
  paths['/admin/events/{eventUuid}/pl/books']['get']['responses'][200]['content']
>['application/json'];
type AdminEventPrimaryPlBookResponse = NonNullable<
  paths['/admin/events/{eventUuid}/pl/primary-book']['post']['responses'][200]['content']
>['application/json'];
type AdminPlBookResponse = NonNullable<
  paths['/admin/pl/books/{bookId}']['get']['responses'][200]['content']
>['application/json'];
type AdminPlBookEntriesResponse = NonNullable<
  paths['/admin/pl/books/{bookId}/entries']['get']['responses'][200]['content']
>['application/json'];
type AdminPlEntryMutationRequest = NonNullable<
  paths['/admin/pl/books/{bookId}/entries']['post']['requestBody']
>['content']['application/json'];
type AdminPlEntryMutationResponse = NonNullable<
  paths['/admin/pl/books/{bookId}/entries']['post']['responses'][201]['content']
>['application/json'];
type AdminPlRecalculateResponse = NonNullable<
  paths['/admin/pl/books/{bookId}/recalculate']['post']['responses'][200]['content']
>['application/json'];
type AdminPlBookSummaryResponse = NonNullable<
  paths['/admin/pl/books/{bookId}/summary']['get']['responses'][200]['content']
>['application/json'];
type MaterialListResponse = NonNullable<
  paths['/events/{eventUuid}/materials']['get']['responses'][200]['content']
>['application/json'];
type MaterialListQuery = NonNullable<
  operations['listEventMaterials']['parameters']['query']
>;
type EventMaterialMutationRequest = NonNullable<
  paths['/events/{eventUuid}/materials']['post']['requestBody']
>['content']['application/json'];
type EventMaterialPatchRequest = NonNullable<
  paths['/events/{eventUuid}/materials/{materialId}']['put']['requestBody']
>['content']['application/json'];
type AdminEventFlyerUploadRequest = NonNullable<
  paths['/admin/events/{eventUuid}/flyers/{face}']['post']['requestBody']
>['content']['multipart/form-data'];
type EventPerformanceFlyerUploadRequest = NonNullable<
  paths['/admin/events/{eventUuid}/performances/{performanceId}/flyer']['post']['requestBody']
>['content']['multipart/form-data'];
type AdminEventBannerUploadRequest = NonNullable<
  paths['/admin/events/{eventUuid}/banner']['post']['requestBody']
>['content']['multipart/form-data'];

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const API_ORIGIN = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');

const eventDetailFetchJson = async (
  path: string,
  init: RequestInit = {},
  fallbackMessage = 'イベント情報の更新に失敗しました。'
): Promise<Record<string, unknown>> => {
  const result = await requestApiJson(path, {
    method: init.method || 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
    body: init.body,
    signal: init.signal || undefined,
    cache: init.cache || undefined,
  });
  const payload = asRecord(result.data);
  if (!result.response.ok) {
    throwStatusError(result.response.status, result.error || payload, fallbackMessage);
  }
  return payload;
};

const parseTextJson = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value !== 'string' || !value.trim()) return {};
  const parsed = JSON.parse(value);
  return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
};

const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!error) return undefined;
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        const message = record.message || record.error;
        const details = typeof record.details === 'string' ? record.details.trim() : '';
        if (typeof message === 'string') return details ? `${message} (${details})` : message;
      }
    } catch {
      return error;
    }
  }
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  const details = typeof record.details === 'string' ? record.details.trim() : '';
  return typeof message === 'string' ? (details ? `${message} (${details})` : message) : undefined;
};

const throwStatusError = (status: number, error?: unknown, fallbackMessage?: string): never => {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage || statusError.message;
  throw statusError;
};

const parseApiTextJson = (result: ApiTextResult, fallbackMessage: string): Record<string, unknown> => {
  if (!result.response.ok) {
    throwStatusError(result.response.status, result.error, fallbackMessage);
  }
  try {
    return parseTextJson(result.data);
  } catch {
    throw new Error(fallbackMessage);
  }
};

const requireFilenamePayload = (payload: Record<string, unknown>, fallbackMessage: string): string => {
  const filename = typeof payload.filename === 'string' ? payload.filename : '';
  if (payload.success !== true || !filename) {
    const message = typeof payload.message === 'string' ? payload.message : fallbackMessage;
    throw new Error(message);
  }
  return filename;
};

const requireSuccessPayload = (payload: Record<string, unknown>, fallbackMessage: string): void => {
  if (payload.success !== true) {
    const message = typeof payload.message === 'string' ? payload.message : fallbackMessage;
    throw new Error(message);
  }
};

export async function getAdminStageSeatingSharedSummary(
  eventId: string
): Promise<Record<string, unknown>> {
  const { data, response } = await api.GET('/admin/events/{eventId}/stage-seating/shared-summary', {
    params: { path: { eventId } },
  });
  if (!response.ok) throw new ApiStatusError(response.status);
  return asRecord(data);
}

export async function getEventNotificationSilence(
  eventId: string
): Promise<Record<string, unknown>> {
  return eventDetailFetchJson(
    `/api/admin/events/${encodeURIComponent(eventId)}/notification-silence`,
    { method: 'GET' },
    '通知サイレント設定の取得に失敗しました。'
  );
}

export async function updateEventNotificationSilence(
  eventId: string,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  return eventDetailFetchJson(
    `/api/admin/events/${encodeURIComponent(eventId)}/notification-silence`,
    {
      method: 'PUT',
      body: JSON.stringify(payload || {}),
    },
    '通知サイレント設定の保存に失敗しました。'
  );
}

export async function getArtistStageSeatingSharedSummary(
  eventId: string
): Promise<Record<string, unknown>> {
  const result = await api.GET('/artist/events/{eventId}/stage-seating/shared', {
    params: { path: { eventId } },
    parseAs: 'text',
  });
  return parseApiTextJson(result, '共有シーティング情報の取得に失敗しました。');
}

export async function listEventCastingInfo(eventId: string): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/common/events/{eventId}/casting-info', {
    params: { path: { eventId } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data) ? (data as CastingInfoResponse) : [];
}

export async function getAdminEventCastingProgress(
  eventId: string
): Promise<Record<string, unknown>> {
  const { data, response } = await api.GET('/admin/events/{eventId}/casting-progress', {
    params: { path: { eventId } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return asRecord(data as CastingProgressResponse);
}

export async function addEventManualParticipant(
  eventUuid: string,
  payload: { artistId: string; note?: string | null }
): Promise<Record<string, unknown>> {
  return eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/manual-participants`,
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
    '契約なし参加アーティストの追加に失敗しました。'
  );
}

export async function removeEventManualParticipant(
  eventUuid: string,
  artistId: string
): Promise<Record<string, unknown>> {
  return eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/manual-participants/${encodeURIComponent(artistId)}`,
    { method: 'DELETE' },
    '契約なし参加アーティストの解除に失敗しました。'
  );
}

export async function listAdminEventCastingProgressRows(
  eventId: string
): Promise<Record<string, unknown>[]> {
  const result = await api.GET('/admin/events/{eventId}/casting-progress', {
    params: { path: { eventId } },
    parseAs: 'text',
  });
  const payload = parseApiTextJson(result, '割当情報の取得に失敗しました');
  return Array.isArray(payload) ? (payload as Record<string, unknown>[]) : [];
}

export async function listCommonEventCastingProgress(
  eventId: string
): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/common/events/{eventId}/casting-progress', {
    params: { path: { eventId } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data) ? (data as unknown as Record<string, unknown>[]) : [];
}

export async function getEventGroupEvents(eventUuid: string): Promise<Record<string, unknown>> {
  const { data, response } = await api.GET('/events/{eventUuid}/group-events', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return asRecord(data as EventGroupEventsResponse);
}

export async function getArtistEventGroupEvents(eventUuid: string): Promise<Record<string, unknown>> {
  const result = await api.GET('/artist/events/{eventUuid}/group-events', {
    params: { path: { eventUuid } },
    parseAs: 'text',
  });
  return parseApiTextJson(result, '関連イベントの取得に失敗しました。');
}

export async function getEventCastings(eventUuid: string): Promise<Record<string, unknown>> {
  const result = await api.GET('/events/{eventUuid}/castings', {
    params: { path: { eventUuid } },
    parseAs: 'text',
  });
  return parseApiTextJson(result, 'キャスティング情報の取得に失敗しました。');
}

export async function updateEventCastings(
  eventUuid: string,
  body: EventCastingUpdateRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/events/{eventUuid}/castings/update', {
    params: { path: { eventUuid } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, 'キャスティング情報の保存に失敗しました。');
}

export async function applyParentEventCastings(eventUuid: string): Promise<Record<string, unknown>> {
  const result = await api.POST('/events/{eventUuid}/castings/apply-parent', {
    params: { path: { eventUuid } },
    parseAs: 'text',
  });
  return parseApiTextJson(result, '親イベントのキャスティング反映に失敗しました。');
}

export async function assignEventCastingArtist(
  eventId: string,
  body: CastingAssignRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{eventId}/casting-assign', {
    params: { path: { eventId } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, 'パート割当の保存に失敗しました');
}

export async function batchAssignEventCastingArtists(
  eventId: string,
  body: BatchCastingAssignRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{eventId}/casting-assign-batch', {
    params: { path: { eventId } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, '一括パート割当の保存に失敗しました');
}

export async function saveEventCastingAssignments(
  eventId: string,
  body: EventCastingAssignmentSaveRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{eventId}/casting-assignment-save', {
    params: { path: { eventId } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, '参加パート設定の保存に失敗しました');
}

export async function updateCastingChief(
  eventId: string,
  body: UpdateCastingChiefRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{eventId}/casting-chief', {
    params: { path: { eventId } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, '首席設定の保存に失敗しました');
}

export async function updateCastingConcertmaster(
  eventId: string,
  body: UpdateCastingConcertmasterRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{eventId}/casting-concertmaster', {
    params: { path: { eventId } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, 'コンサートマスター設定の保存に失敗しました');
}

export async function listEventCustomRoles(eventId: string): Promise<Record<string, unknown>[]> {
  const result = await api.GET('/admin/events/{eventId}/custom-roles', {
    params: { path: { eventId } },
    parseAs: 'text',
  });
  const payload = parseApiTextJson(result, '役職一覧の取得に失敗しました');
  const roles = payload.roles;
  if (Array.isArray(roles)) return roles as Record<string, unknown>[];
  return Array.isArray(payload) ? (payload as Record<string, unknown>[]) : [];
}

export async function createEventCustomRole(
  eventId: string,
  body: EventCustomRoleRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{eventId}/custom-roles', {
    params: { path: { eventId } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, '役職の追加に失敗しました');
}

export async function deleteEventCustomRole(
  eventId: string,
  roleId: string
): Promise<Record<string, unknown>> {
  const result = await api.DELETE('/admin/events/{eventId}/custom-roles/{roleId}', {
    params: { path: { eventId, roleId } },
    parseAs: 'text',
  });
  return parseApiTextJson(result, '役職の削除に失敗しました');
}

export async function saveEventCustomRoleAssignments(
  eventId: string,
  body: EventCustomRoleAssignmentsRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{eventId}/custom-roles/assignments', {
    params: { path: { eventId } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, '役職割当の保存に失敗しました');
}

export async function listCastingTemplates(): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/casting-templates');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const templates = (data as CastingTemplateListResponse).templates;
  return Array.isArray(templates) ? templates : [];
}

export async function createCastingTemplate(body: CastingTemplateMutationRequest): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.POST('/casting-templates', {
    body,
  });
  if (!response.ok) throwStatusError(response.status, error, 'キャスティングテンプレートの作成に失敗しました。');
  return asRecord(data);
}

export async function applyEventCastingTemplate(
  eventUuid: string,
  templateUuid: string
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.POST(
    '/events/{eventUuid}/casting-templates/{templateUuid}/apply',
    {
      params: { path: { eventUuid, templateUuid } },
    }
  );
  if (!response.ok) throwStatusError(response.status, error, 'キャスティングテンプレートの適用に失敗しました。');
  return asRecord(data);
}

export async function deleteCastingTemplate(templateUuid: string): Promise<void> {
  const { error, response } = await api.DELETE('/casting-templates/{templateUuid}', {
    params: { path: { templateUuid } },
  });
  if (!response.ok) throwStatusError(response.status, error, 'キャスティングテンプレートの削除に失敗しました。');
}

export async function getEventChatGroupSetting(
  groupRootEventUuid: string
): Promise<Record<string, unknown>> {
  const { data, response } = await api.GET('/admin/events/{groupRootEventUuid}/chat-group-setting', {
    params: { path: { groupRootEventUuid } },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return asRecord(data as ChatGroupSettingResponse);
}

export async function previewEventChatGroupSetting(
  groupRootEventUuid: string,
  body: EventChatGroupSettingRequest
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{groupRootEventUuid}/chat-group-setting/preview', {
    params: { path: { groupRootEventUuid } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, 'グループチャット変更プレビューの取得に失敗しました');
}

export async function updateEventChatGroupSetting(
  groupRootEventUuid: string,
  body: EventChatGroupSettingRequest
): Promise<Record<string, unknown>> {
  const result = await api.PUT('/admin/events/{groupRootEventUuid}/chat-group-setting', {
    params: { path: { groupRootEventUuid } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, 'グループチャット設定の更新に失敗しました');
}

export async function cancelAdminEvent(
  eventUuid: string,
  body: EventCancelRequest = {}
): Promise<Record<string, unknown>> {
  const result = await api.POST('/admin/events/{eventUuid}/cancel', {
    params: { path: { eventUuid } },
    body,
    parseAs: 'text',
  });
  return parseApiTextJson(result, 'イベントの中止に失敗しました');
}

export async function listAdminEventPlBooks(
  eventUuid: string
): Promise<Record<string, unknown> | null> {
  const { data, response } = await api.GET('/admin/events/{eventUuid}/pl/books', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) return null;
  return asRecord(data as AdminEventPlBooksResponse);
}

export async function resolveAdminEventPrimaryPlBook(
  eventUuid: string
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.POST('/admin/events/{eventUuid}/pl/primary-book', {
    params: { path: { eventUuid } },
  });
  if (!response.ok || !data) {
    throwStatusError(response.status, error, 'イベント損益データの取得に失敗しました。');
  }
  return asRecord(data as AdminEventPrimaryPlBookResponse);
}

export async function getAdminPlBook(bookId: string): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.GET('/admin/pl/books/{bookId}', {
    params: { path: { bookId } },
  });
  if (!response.ok || !data) {
    throwStatusError(response.status, error, '損益ブックの取得に失敗しました。');
  }
  return asRecord(data as AdminPlBookResponse);
}

export async function listAdminPlBookEntries(
  bookId: string
): Promise<Record<string, unknown>[]> {
  const { data, error, response } = await api.GET('/admin/pl/books/{bookId}/entries', {
    params: { path: { bookId } },
  });
  if (!response.ok || !data) {
    throwStatusError(response.status, error, '損益明細の取得に失敗しました。');
  }
  const entries = (data as AdminPlBookEntriesResponse).entries;
  return Array.isArray(entries) ? entries : [];
}

export async function createAdminPlBookEntry(
  bookId: string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.POST('/admin/pl/books/{bookId}/entries', {
    params: { path: { bookId } },
    body: body as AdminPlEntryMutationRequest,
  });
  if (!response.ok || !data) {
    throwStatusError(response.status, error, '損益明細の追加に失敗しました。');
  }
  return asRecord(data as AdminPlEntryMutationResponse);
}

export async function deleteAdminPlEntry(entryId: string): Promise<void> {
  const { error, response } = await api.DELETE('/admin/pl/entries/{entryId}', {
    params: { path: { entryId } },
  });
  if (!response.ok) {
    throwStatusError(response.status, error, '損益明細の削除に失敗しました。');
  }
}

export async function recalculateAdminPlBook(
  bookId: string
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.POST('/admin/pl/books/{bookId}/recalculate', {
    params: { path: { bookId } },
  });
  if (!response.ok || !data) {
    throwStatusError(response.status, error, '再計算に失敗しました。');
  }
  return asRecord(data as AdminPlRecalculateResponse);
}

export async function getAdminPlBookSummary(
  bookId: string
): Promise<Record<string, unknown> | null> {
  const { data, response } = await api.GET('/admin/pl/books/{bookId}/summary', {
    params: { path: { bookId } },
  });
  if (!response.ok || !data) return null;
  return asRecord(data as AdminPlBookSummaryResponse);
}

export async function getAdminPlBookSummaryOrThrow(
  bookId: string
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.GET('/admin/pl/books/{bookId}/summary', {
    params: { path: { bookId } },
  });
  if (!response.ok || !data) {
    throwStatusError(response.status, error, '損益サマリーの取得に失敗しました。');
  }
  return asRecord(data as AdminPlBookSummaryResponse);
}

export async function listEventMaterials(
  eventUuid: string,
  query: MaterialListQuery = {}
): Promise<Record<string, unknown>[]> {
  const { data, response } = await api.GET('/events/{eventUuid}/materials', {
    params: { path: { eventUuid }, query },
  });
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  const materials = (data as MaterialListResponse).materials;
  return Array.isArray(materials) ? materials : [];
}

export async function getEventMaterialTree(
  eventUuid: string,
  folderId?: number | string | null
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams();
  if (folderId !== undefined && folderId !== null && folderId !== '') {
    params.set('folderId', String(folderId));
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/materials/tree${suffix}`,
    {},
    '資料フォルダの取得に失敗しました'
  );
}

export async function createEventMaterialFolder(
  eventUuid: string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/material-folders`,
    { method: 'POST', body: JSON.stringify(body) },
    'フォルダの作成に失敗しました'
  );
  return asRecord(data.folder);
}

export async function getEventImageSummary(eventUuid: string): Promise<Record<string, unknown>> {
  return eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/event-images/summary`,
    {},
    'イベントアルバムの取得に失敗しました'
  );
}

export async function getEventImageTree(
  eventUuid: string,
  folderId?: number | string | null
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams();
  if (folderId !== undefined && folderId !== null && folderId !== '') {
    params.set('folderId', String(folderId));
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/event-images/tree${suffix}`,
    {},
    'イベントアルバムフォルダの取得に失敗しました'
  );
}

export async function createEventImageFolder(
  eventUuid: string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/event-image-folders`,
    { method: 'POST', body: JSON.stringify(body) },
    'フォルダの作成に失敗しました'
  );
  return asRecord(data.folder);
}

export async function createEventImageBatch(
  eventUuid: string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  return eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/event-images/batches`,
    { method: 'POST', body: JSON.stringify(body) },
    'イベントアルバムへの追加に失敗しました'
  );
}

export async function moveEventImages(
  eventUuid: string,
  imageIds: Array<number | string>,
  folderId: number | string | null
): Promise<Record<string, unknown>[]> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/event-images/move`,
    { method: 'PATCH', body: JSON.stringify({ image_ids: imageIds, folder_id: folderId }) },
    'イベントアルバム画像の移動に失敗しました'
  );
  const images = data.images;
  return Array.isArray(images) ? images.filter((item): item is Record<string, unknown> => !!item && typeof item === 'object') : [];
}

export async function deleteEventImages(
  eventUuid: string,
  imageIds: Array<number | string>
): Promise<void> {
  await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/event-images`,
    { method: 'DELETE', body: JSON.stringify({ image_ids: imageIds }) },
    'イベントアルバム画像の削除に失敗しました'
  );
}

export function buildEventImageFileUrl(
  eventUuid: string,
  imageId: number | string,
  { download = false }: { download?: boolean } = {}
): string {
  const query = download ? '?download=1' : '';
  return `${API_ORIGIN}/api/events/${encodeURIComponent(eventUuid)}/event-images/${encodeURIComponent(String(imageId))}/file${query}`;
}

export async function updateEventMaterialFolder(
  eventUuid: string,
  folderId: number | string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/material-folders/${encodeURIComponent(String(folderId))}`,
    { method: 'PUT', body: JSON.stringify(body) },
    'フォルダの更新に失敗しました'
  );
  return asRecord(data.folder);
}

export async function moveEventMaterialFolder(
  eventUuid: string,
  folderId: number | string,
  parentFolderId: number | string | null
): Promise<Record<string, unknown>> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/material-folders/${encodeURIComponent(String(folderId))}/move`,
    { method: 'PATCH', body: JSON.stringify({ parent_folder_id: parentFolderId }) },
    'フォルダの移動に失敗しました'
  );
  return asRecord(data.folder);
}

export async function deleteEventMaterialFolder(
  eventUuid: string,
  folderId: number | string
): Promise<void> {
  await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/material-folders/${encodeURIComponent(String(folderId))}`,
    { method: 'DELETE' },
    'フォルダの削除に失敗しました'
  );
}

export async function createEventMaterial(
  eventUuid: string,
  body: EventMaterialMutationRequest
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.POST('/events/{eventUuid}/materials', {
    params: { path: { eventUuid } },
    body,
  });
  if (!response.ok || !data) {
    throwStatusError(response.status, error, '資料の登録に失敗しました');
  }
  const material = (data as { material?: unknown }).material;
  if (!material || typeof material !== 'object') throw new Error('資料の登録に失敗しました');
  return material as Record<string, unknown>;
}

export async function updateEventMaterial(
  eventUuid: string,
  materialId: number | string,
  body: EventMaterialPatchRequest
): Promise<Record<string, unknown>> {
  const { data, error, response } = await api.PUT('/events/{eventUuid}/materials/{materialId}', {
    params: { path: { eventUuid, materialId } },
    body,
  });
  if (!response.ok || !data) {
    throwStatusError(response.status, error, '資料の更新に失敗しました');
  }
  const material = (data as { material?: unknown }).material;
  if (!material || typeof material !== 'object') throw new Error('資料の更新に失敗しました');
  return material as Record<string, unknown>;
}

export async function deleteEventMaterial(
  eventUuid: string,
  materialId: number | string
): Promise<void> {
  const { error, response } = await api.DELETE('/events/{eventUuid}/materials/{materialId}', {
    params: { path: { eventUuid, materialId } },
  });
  if (!response.ok) throwStatusError(response.status, error, '資料の削除に失敗しました');
}

export async function moveEventMaterial(
  eventUuid: string,
  materialId: number | string,
  folderId: number | string | null
): Promise<Record<string, unknown>> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/materials/${encodeURIComponent(String(materialId))}/move`,
    { method: 'PATCH', body: JSON.stringify({ folder_id: folderId }) },
    '資料の移動に失敗しました'
  );
  return asRecord(data.material);
}

export async function replaceEventMaterialVersion(
  eventUuid: string,
  materialId: number | string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/materials/${encodeURIComponent(String(materialId))}/versions`,
    { method: 'POST', body: JSON.stringify(body) },
    '資料の差し替えに失敗しました'
  );
  return asRecord(data.material);
}

export async function listEventMaterialVersions(
  eventUuid: string,
  materialId: number | string
): Promise<Record<string, unknown>[]> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/materials/${encodeURIComponent(String(materialId))}/versions`,
    {},
    '資料履歴の取得に失敗しました'
  );
  return Array.isArray(data.versions) ? (data.versions as Record<string, unknown>[]) : [];
}

export async function listEventMaterialDownloadLogs(
  eventUuid: string,
  materialId: number | string
): Promise<Record<string, unknown>[]> {
  const data = await eventDetailFetchJson(
    `/api/events/${encodeURIComponent(eventUuid)}/materials/${encodeURIComponent(String(materialId))}/download-logs`,
    {},
    'ダウンロード履歴の取得に失敗しました'
  );
  return Array.isArray(data.logs) ? (data.logs as Record<string, unknown>[]) : [];
}

export async function uploadAdminEventFlyer(
  eventUuid: string,
  face: 'front' | 'back',
  body: AdminEventFlyerUploadRequest
): Promise<string> {
  const result = await api.POST('/admin/events/{eventUuid}/flyers/{face}', {
    params: { path: { eventUuid, face } },
    body,
    parseAs: 'text',
  });
  const payload = parseApiTextJson(result, 'フライヤーの更新に失敗しました');
  return requireFilenamePayload(payload, 'アップロード結果のファイル名を取得できませんでした');
}

export async function deleteAdminEventFlyer(
  eventUuid: string,
  face: 'front' | 'back'
): Promise<void> {
  const result = await api.DELETE('/admin/events/{eventUuid}/flyers/{face}', {
    params: { path: { eventUuid, face } },
    parseAs: 'text',
  });
  const payload = parseApiTextJson(result, 'フライヤーの削除に失敗しました');
  requireSuccessPayload(payload, 'フライヤーの削除に失敗しました');
}

export async function uploadEventPerformanceFlyer(
  eventUuid: string,
  performanceId: number | string,
  body: EventPerformanceFlyerUploadRequest
): Promise<string> {
  const result = await api.POST('/admin/events/{eventUuid}/performances/{performanceId}/flyer', {
    params: { path: { eventUuid, performanceId } },
    body,
    parseAs: 'text',
  });
  const payload = parseApiTextJson(result, '公演フライヤーの更新に失敗しました');
  return requireFilenamePayload(payload, 'アップロード結果のファイル名を取得できませんでした');
}

export async function deleteEventPerformanceFlyer(
  eventUuid: string,
  performanceId: number | string
): Promise<void> {
  const result = await api.DELETE('/admin/events/{eventUuid}/performances/{performanceId}/flyer', {
    params: { path: { eventUuid, performanceId } },
    parseAs: 'text',
  });
  const payload = parseApiTextJson(result, '公演フライヤーの削除に失敗しました');
  requireSuccessPayload(payload, '公演フライヤーの削除に失敗しました');
}

export async function uploadAdminEventBanner(
  eventUuid: string,
  body: AdminEventBannerUploadRequest
): Promise<string> {
  const result = await api.POST('/admin/events/{eventUuid}/banner', {
    params: { path: { eventUuid } },
    body,
    parseAs: 'text',
  });
  const payload = parseApiTextJson(result, 'バナーの更新に失敗しました');
  return requireFilenamePayload(payload, 'アップロード結果のファイル名を取得できませんでした');
}

export async function deleteAdminEventBanner(eventUuid: string): Promise<void> {
  const result = await api.DELETE('/admin/events/{eventUuid}/banner', {
    params: { path: { eventUuid } },
    parseAs: 'text',
  });
  const payload = parseApiTextJson(result, 'バナーの削除に失敗しました');
  requireSuccessPayload(payload, 'バナーの削除に失敗しました');
}
